import { CrossOriginXsrfConfig } from './xsrf.config';

type XsrfResolution =
  | { kind: 'skip' }
  | { kind: 'credentials-only' }
  | { kind: 'apply'; headerName: string; token: string };

/**
 * Returns xsrf token to include in the headers.
 *
 * This utility was introduces to be able to ensure
 * XSRF calls with httpClient API but also with direct xhr
 * calls (alas legacy lib ng2-file-upload :( )
 *
 * @param url url that will be called by the httpClient / xhr
 * @param method request.method (POST, etc...)
 * @param config xhr config
 * @param locationHref browser current location
 */
export function resolveXsrfHeader(
  url: string,
  method: string,
  config: CrossOriginXsrfConfig,
  locationHref: string,
): XsrfResolution {
  // Do not send csrf token to untrusted domains
  if (!isTrustedOrigin(url, config, locationHref)) {
    return { kind: 'skip' };
  }

  // At this point, we trust the origin → we want cookies
  if (!isMutating(method)) {
    return { kind: 'credentials-only' };
  }

  const token = readCookie(config.cookieName) ?? undefined;

  if (!token) {
    return { kind: 'credentials-only' };
  }

  return {
    kind: 'apply',
    headerName: config.headerName,
    token,
  };
}

function isMutating(method: string) {
  return method !== 'GET' && method !== 'HEAD';
}

function isTrustedOrigin(url: string, config: CrossOriginXsrfConfig, locationHref: string): boolean {
  try {
    const { origin } = new URL(locationHref);
    const { hostname } = new URL(url, origin);
    return config.trustedApiOrigins.some(trusted => hostname.endsWith(trusted));
  } catch {
    return false;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;

  return (
    document.cookie
      .split('; ')
      .find(c => c.startsWith(`${name}=`))
      ?.substring(name.length + 1) ?? null
  );
}
